import {
	AlertTriangle,
	CalendarCheck,
	ClipboardCheck,
	MessageSquareText,
	Mic,
	ShieldAlert,
} from 'lucide-react'
import { usePersonTimeline } from '@/api/queries/commitment-matrix-queries'
import { Badge } from '@/components/ui/badge'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { INSPECTION_TYPE_BAR_CLASS } from '@/constants/inspection-type-chart-colors'
import { cn } from '@/lib/utils'
import type { DateRange } from '@/types'
import { formatDateToDisplay } from '@/utils/formatDate'
import { CartaoAmareloTimelineSection } from './timeline-sections/cartao-amarelo-timeline-section'
import { DdsTimelineSection } from './timeline-sections/dds-timeline-section'
import { DirecionadaLivreTimelineSection } from './timeline-sections/direcionada-livre-timeline-section'
import { DirecionadaTimelineSection } from './timeline-sections/direcionada-timeline-section'
import { ExceptionsTimelineSection } from './timeline-sections/exceptions-timeline-section'
import { InteracaoTimelineSection } from './timeline-sections/interacao-timeline-section'
import { VereAgirTimelineSection } from './timeline-sections/vere-agir-timeline-section'

const TIMELINE_TAB_STYLE = {
	vereAgir: {
		text: 'text-kaefer-forest-green',
		bar: INSPECTION_TYPE_BAR_CLASS['see-act'],
	},
	cartaoAmarelo: {
		text: 'text-kaefer-dark-grey',
		bar: INSPECTION_TYPE_BAR_CLASS['yellow-card'],
	},
	interacao: {
		text: 'text-kaefer-violet',
		bar: INSPECTION_TYPE_BAR_CLASS.interaction,
	},
	direcionada: {
		text: 'text-kaefer-ocean-blue',
		bar: INSPECTION_TYPE_BAR_CLASS.directed,
	},
	dds: {
		text: 'text-kaefer-sapphire-blue',
		bar: 'bg-kaefer-sapphire-blue',
	},
	exceptions: {
		text: 'text-kaefer-pumpkin-orange',
		bar: 'bg-kaefer-pumpkin-orange',
	},
} as const

interface PersonTimelineModalProps {
	personId: string | null
	range: DateRange
	onClose: () => void
}

export function PersonTimelineModal({
	personId,
	range,
	onClose,
}: PersonTimelineModalProps) {
	const params = {
		rangeStart: range.start.toISOString().slice(0, 10),
		rangeEnd: range.end.toISOString().slice(0, 10),
	}

	const { data, isLoading } = usePersonTimeline(personId, params)
	const approvedExceptions = data?.exceptions.byStatus.APROVADO ?? 0

	return (
		<Dialog open={!!personId} onOpenChange={(next) => !next && onClose()}>
			<DialogContent className="flex h-dvh w-screen max-w-none flex-col gap-0 rounded-none border-0 p-0 sm:h-[92vh] sm:w-[80vw] sm:max-w-[80vw] sm:rounded-lg sm:border">
				{isLoading || !data ? (
					<PersonTimelineModalLoading />
				) : (
					<>
						<DialogHeader className="shrink-0 border-b border-kaefer-light-silver px-4 py-3 sm:px-6">
							<DialogTitle className="text-base text-kaefer-dark-grey">
								{data.person.name}
							</DialogTitle>
							<p className="text-xs text-muted-foreground">
								{data.person.registration} · {data.person.jobRole ?? '—'} ·{' '}
								{data.person.branchName} · {data.person.crName}
							</p>
							<p className="text-xs text-muted-foreground">
								Período: {formatDateToDisplay(data.period.rangeStart)} –{' '}
								{formatDateToDisplay(data.period.rangeEnd)}
							</p>
						</DialogHeader>

						<div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
							<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
								<SummaryTile
									icon={<ShieldAlert className="size-4" />}
									label="Ver e Agir"
									value={
										data.vereAgir.created.total +
										data.vereAgir.asResponsible.total
									}
									style={TIMELINE_TAB_STYLE.vereAgir}
								/>
								<SummaryTile
									icon={<AlertTriangle className="size-4" />}
									label="Cartão Amarelo"
									value={data.cartaoAmarelo.total}
									style={TIMELINE_TAB_STYLE.cartaoAmarelo}
								/>
								<SummaryTile
									icon={<MessageSquareText className="size-4" />}
									label="Interação"
									value={data.interacao.total}
									style={TIMELINE_TAB_STYLE.interacao}
								/>
								<SummaryTile
									icon={<ClipboardCheck className="size-4" />}
									label="Direcionada"
									value={data.direcionada.total}
									highlight={`${data.direcionada.compliancePct}% conforme`}
									style={TIMELINE_TAB_STYLE.direcionada}
								/>
								<SummaryTile
									icon={<Mic className="size-4" />}
									label="DDS"
									value={data.dds.ministered.total + data.dds.attended.total}
									style={TIMELINE_TAB_STYLE.dds}
								/>
								<SummaryTile
									icon={<CalendarCheck className="size-4" />}
									label="Abonos"
									value={data.exceptions.total}
									highlight={
										approvedExceptions > 0
											? `${approvedExceptions} aprovado${approvedExceptions > 1 ? 's' : ''}`
											: undefined
									}
									style={TIMELINE_TAB_STYLE.exceptions}
								/>
							</div>

							<Tabs defaultValue="vereAgir" className="mt-4">
								<TabsList className="w-fit flex-wrap">
									<TabsTrigger value="vereAgir" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.vereAgir.bar} />
										Ver e Agir
										<Badge variant="secondary">
											{data.vereAgir.created.total +
												data.vereAgir.asResponsible.total}
										</Badge>
									</TabsTrigger>
									<TabsTrigger value="cartaoAmarelo" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.cartaoAmarelo.bar} />
										Cartão Amarelo
										<Badge variant="secondary">
											{data.cartaoAmarelo.total}
										</Badge>
									</TabsTrigger>
									<TabsTrigger value="interacao" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.interacao.bar} />
										Interação
										<Badge variant="secondary">{data.interacao.total}</Badge>
									</TabsTrigger>
									<TabsTrigger value="direcionada" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.direcionada.bar} />
										Direcionada
										<Badge variant="secondary">{data.direcionada.total}</Badge>
									</TabsTrigger>
									<TabsTrigger value="direcionadaLivre" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.direcionada.bar} />
										Direcionada Livre
										<Badge variant="secondary">
											{data.direcionadaLivre.total}
										</Badge>
									</TabsTrigger>
									<TabsTrigger value="dds" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.dds.bar} />
										DDS
										<Badge variant="secondary">
											{data.dds.ministered.total + data.dds.attended.total}
										</Badge>
									</TabsTrigger>
									<TabsTrigger value="exceptions" className="gap-1.5">
										<TabDot bar={TIMELINE_TAB_STYLE.exceptions.bar} />
										Abonos
										<Badge variant="secondary">{data.exceptions.total}</Badge>
									</TabsTrigger>
								</TabsList>

								<TabsContent value="vereAgir" className="pt-4">
									<VereAgirTimelineSection data={data.vereAgir} />
								</TabsContent>
								<TabsContent value="cartaoAmarelo" className="pt-4">
									<CartaoAmareloTimelineSection data={data.cartaoAmarelo} />
								</TabsContent>
								<TabsContent value="interacao" className="pt-4">
									<InteracaoTimelineSection data={data.interacao} />
								</TabsContent>
								<TabsContent value="direcionada" className="pt-4">
									<DirecionadaTimelineSection data={data.direcionada} />
								</TabsContent>
								<TabsContent value="direcionadaLivre" className="pt-4">
									<DirecionadaLivreTimelineSection
										data={data.direcionadaLivre}
									/>
								</TabsContent>
								<TabsContent value="dds" className="pt-4">
									<DdsTimelineSection data={data.dds} />
								</TabsContent>
								<TabsContent value="exceptions" className="pt-4">
									<ExceptionsTimelineSection
										data={data.exceptions}
										period={data.period}
									/>
								</TabsContent>
							</Tabs>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}

function SummaryTile({
	icon,
	label,
	value,
	highlight,
	style,
}: {
	icon: React.ReactNode
	label: string
	value: number
	highlight?: string
	style: { text: string; bar: string }
}) {
	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-3">
			<div className={cn('flex items-center gap-1.5', style.text)}>
				{icon}
				<span className="text-xs text-muted-foreground">{label}</span>
			</div>
			<p className={cn('mt-1 text-xl font-semibold', style.text)}>{value}</p>
			{highlight && (
				<p className="text-xs font-medium text-kaefer-forest-green">
					{highlight}
				</p>
			)}
			<div className={cn('mt-2 h-1 w-8 rounded-full', style.bar)} />
		</div>
	)
}

function TabDot({ bar }: { bar: string }) {
	return <span className={cn('size-2 shrink-0 rounded-full', bar)} />
}

function PersonTimelineModalLoading() {
	return (
		<div className="flex flex-col gap-4 p-6">
			<Skeleton className="h-6 w-56" />
			<Skeleton className="h-4 w-72" />
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
				{['a', 'b', 'c', 'd', 'e', 'f'].map((key) => (
					<Skeleton key={key} className="h-20 w-full rounded-lg" />
				))}
			</div>
			<Skeleton className="h-64 w-full rounded-lg" />
		</div>
	)
}

export function TimelineNameCountList({
	items,
	emptyLabel,
}: {
	items: { id: string; name: string; count: number }[]
	emptyLabel: string
}) {
	if (items.length === 0) {
		return <p className="text-xs text-muted-foreground">{emptyLabel}</p>
	}
	return (
		<ul className="flex flex-col gap-1">
			{items.map((item) => (
				<li key={item.id} className="flex items-center justify-between text-xs">
					<span className="text-kaefer-dark-grey">{item.name}</span>
					<span className="font-semibold text-muted-foreground">
						{item.count}
					</span>
				</li>
			))}
		</ul>
	)
}

export function TimelineEmptyState({ label }: { label: string }) {
	return (
		<p className="rounded-lg border border-kaefer-light-silver bg-white py-8 text-center text-sm text-muted-foreground">
			{label}
		</p>
	)
}

export function TimelineSectionHeading({
	children,
	className,
}: {
	children: React.ReactNode
	className?: string
}) {
	return (
		<h4
			className={cn(
				'text-xs font-semibold uppercase tracking-wide text-muted-foreground',
				className,
			)}
		>
			{children}
		</h4>
	)
}
